import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { canViewFinancial, formatINR } from "@/lib/permissions";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Package, TrendingDown, Wrench, AlertTriangle,
  IndianRupee, Layers, ChevronRight, CalendarClock,
} from "lucide-react";
import { NavLink } from "react-router-dom";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from "recharts";
import { AskAssetIQ, PredictiveInsights } from "@/components/AIFeatures";

const CHART_COLORS = ["#7C1D3A", "#B8355A", "#E07897", "#F5C4D3", "#D97706", "#2563EB"];

function KpiCard({ title, value, sub, icon: Icon, tone = "default", testid }) {
  const toneClasses = {
    default: "bg-white",
    warning: "bg-amber-50 border-amber-200",
    danger: "bg-red-50 border-red-200",
    primary: "bg-[#fcf0f3] border-[#f5c4d3]",
  }[tone];
  return (
    <Card className={`${toneClasses} card-hover`} data-testid={testid}>
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs font-semibold tracking-wider uppercase text-slate-500">{title}</p>
            <p className="mt-3 font-heading text-3xl font-bold text-slate-900" data-testid={`${testid}-value`}>
              {value}
            </p>
            {sub && <p className="mt-1 text-xs text-slate-500">{sub}</p>}
          </div>
          <div className="w-10 h-10 rounded-lg bg-white border border-slate-200 flex items-center justify-center shrink-0">
            <Icon className="w-5 h-5 text-[#7C1D3A]" strokeWidth={2} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [upcoming, setUpcoming] = useState([]);
  const [loading, setLoading] = useState(true);
  const showFinancial = canViewFinancial(user?.role);

  useEffect(() => {
    (async () => {
      try {
        const [s, u] = await Promise.all([
          api.get("/dashboard/stats"),
          api.get("/maintenance/upcoming"),
        ]);
        setStats(s.data);
        setUpcoming(u.data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return <div className="text-slate-500">Loading dashboard...</div>;
  }

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs font-semibold tracking-widest uppercase text-[#7C1D3A]">
            Overview · {new Date().toLocaleDateString("en-IN", { weekday: "long", timeZone: "Asia/Kolkata" })}
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mt-2">
            Good {new Date().getHours() < 12 ? "morning" : new Date().getHours() < 18 ? "afternoon" : "evening"}, {user?.name?.split(" ")[0]}
          </h1>
          <p className="text-slate-500 mt-1">Here's a real-time snapshot of your fixed asset portfolio.</p>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          testid="kpi-total-assets"
          title="Total Assets"
          value={stats.total_assets}
          sub={`${stats.by_category.length} categories tracked`}
          icon={Package}
          tone="primary"
        />
        {showFinancial ? (
          <>
            <KpiCard
              testid="kpi-total-value"
              title="Current Book Value"
              value={formatINR(stats.total_current_value)}
              sub={`of ${formatINR(stats.total_purchase_value)} purchase`}
              icon={IndianRupee}
            />
            <KpiCard
              testid="kpi-total-depreciation"
              title="Total Depreciation"
              value={formatINR(stats.total_depreciation)}
              sub="Accumulated to date"
              icon={TrendingDown}
            />
          </>
        ) : (
          <>
            <KpiCard
              testid="kpi-active"
              title="Active Assets"
              value={stats.by_status.active || 0}
              sub="Currently in service"
              icon={Layers}
            />
            <KpiCard
              testid="kpi-retired"
              title="Retired / Disposed"
              value={(stats.by_status.retired || 0) + (stats.by_status.disposed || 0)}
              sub="End-of-life assets"
              icon={Layers}
            />
          </>
        )}
        <KpiCard
          testid="kpi-maintenance"
          title="Under Maintenance"
          value={stats.under_maintenance}
          sub={`${stats.upcoming_maintenance} due in 30 days`}
          icon={Wrench}
          tone={stats.under_maintenance > 0 ? "warning" : "default"}
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Category value or count */}
        <Card className="lg:col-span-2">
          <CardHeader className="border-b border-slate-100">
            <CardTitle className="font-heading text-base">
              {showFinancial ? "Value by Category" : "Assets by Category"}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <ResponsiveContainer width="100%" height={280}>
              {showFinancial && stats.category_values?.length > 0 ? (
                <BarChart data={stats.category_values}>
                  <XAxis dataKey="category" tick={{ fontSize: 11 }} />
                  <YAxis tickFormatter={(v) => `₹${(v / 100000).toFixed(0)}L`} tick={{ fontSize: 11 }} />
                  <Tooltip
                    formatter={(v) => formatINR(v)}
                    contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0" }}
                  />
                  <Legend />
                  <Bar dataKey="purchase" fill="#B8355A" name="Purchase" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="current" fill="#7C1D3A" name="Current Value" radius={[4, 4, 0, 0]} />
                </BarChart>
              ) : (
                <BarChart data={stats.by_category}>
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0" }} />
                  <Bar dataKey="value" fill="#7C1D3A" radius={[4, 4, 0, 0]} />
                </BarChart>
              )}
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Status distribution */}
        <Card>
          <CardHeader className="border-b border-slate-100">
            <CardTitle className="font-heading text-base">Status Distribution</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={Object.entries(stats.by_status).map(([k, v]) => ({ name: k, value: v })).filter(x => x.value > 0)}
                  cx="50%"
                  cy="50%"
                  outerRadius={90}
                  paddingAngle={2}
                  dataKey="value"
                  label={({ name, value }) => `${value}`}
                >
                  {Object.keys(stats.by_status).map((statusKey, i) => (
                    <Cell key={statusKey} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0" }} />
                <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* AI insights row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <PredictiveInsights />
        <AskAssetIQ />
      </div>

      {/* Upcoming Maintenance */}
      <Card data-testid="upcoming-maintenance">
        <CardHeader className="border-b border-slate-100 flex flex-row items-center justify-between">
          <CardTitle className="font-heading text-base flex items-center gap-2">
            <CalendarClock className="w-4 h-4 text-[#7C1D3A]" />
            Upcoming Preventive Maintenance
          </CardTitle>
          <NavLink to="/maintenance" className="text-xs font-medium text-[#7C1D3A] hover:underline flex items-center gap-1">
            View all <ChevronRight className="w-3 h-3" />
          </NavLink>
        </CardHeader>
        <CardContent className="p-0">
          {upcoming.length === 0 ? (
            <div className="p-8 text-center text-slate-500 text-sm">
              <AlertTriangle className="w-8 h-8 mx-auto mb-2 text-slate-300" />
              No upcoming maintenance in the schedule.
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {upcoming.slice(0, 5).map((log) => (
                <NavLink
                  key={log.id}
                  to={`/assets/${log.asset_id}`}
                  className="flex items-center justify-between px-6 py-4 hover:bg-slate-50 transition-colors"
                >
                  <div className="flex items-center gap-4 min-w-0">
                    <div className="w-10 h-10 rounded-lg bg-[#fcf0f3] flex items-center justify-center shrink-0">
                      <Wrench className="w-4 h-4 text-[#7C1D3A]" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-slate-900 truncate">{log.asset_name}</div>
                      <div className="text-xs text-slate-500 font-mono">{log.asset_code} · {log.description}</div>
                    </div>
                  </div>
                  <div className="text-right shrink-0 ml-4">
                    <Badge variant="outline" className="border-amber-200 bg-amber-50 text-amber-700 font-mono text-xs">
                      Due {new Date(log.next_due_date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", timeZone: "Asia/Kolkata" })}
                    </Badge>
                  </div>
                </NavLink>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
